/**
 * LoadingSpinner Component
 *
 * Reusable loading spinner with multiple sizes and variants.
 * Provides consistent loading states across the application.
 *
 * Usage:
 *   <LoadingSpinner size="md" variant="primary" />
 *   <LoadingSpinner size="lg" text="Loading..." />
 */

import React from 'react';

export interface LoadingSpinnerProps {
  /** Size of the spinner */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Visual variant */
  variant?: 'primary' | 'white' | 'gray';
  /** Optional text to display below spinner */
  text?: string;
  /** Center spinner in container */
  centered?: boolean;
  /** Full screen overlay */
  fullScreen?: boolean;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  variant = 'primary',
  text,
  centered = false,
  fullScreen = false,
}) => {
  // Size configurations
  const sizes = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
  };

  // Variant colors
  const variants = {
    primary: 'border-primary',
    white: 'border-white',
    gray: 'border-gray-400',
  };

  // Text sizes for different spinner sizes
  const textSizes = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
  };

  const spinner = (
    <div className={`inline-block ${centered || fullScreen ? 'mx-auto' : ''}`}>
      <div
        className={`
          ${sizes[size]}
          animate-spin rounded-full
          border-2 border-line dark:border-border-dark
          border-t-2 ${variants[variant]}
        `.trim().replace(/\s+/g, ' ')}
        role="status"
        aria-label={text || 'Loading'}
      >
        <span className="sr-only">{text || 'Loading...'}</span>
      </div>
      {text && (
        <p className={`mt-3 ${textSizes[size]} text-ink-2 text-center`}>
          {text}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="flex items-center justify-center h-screen bg-background-light dark:bg-background-dark">
        <div className="text-center">{spinner}</div>
      </div>
    );
  }

  if (centered) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-center">{spinner}</div>
      </div>
    );
  }

  return spinner;
};

export default LoadingSpinner;
